import React, { useEffect, useRef } from "react";
import { FaUser, FaBuilding,} from "react-icons/fa";

const CREATOR_FEATURES = [
  "Connect Instagram and grow with brand campaigns",
  "Automate DMs and reply to comments instantly",
  "Generate trackable affiliate links and earn commissions",
  "Unlock AI insights on audience sentiment",
  "Get paid securely for approved deliverables",
];

const BRAND_FEATURES = [
  "Discover verified creators for your campaigns",
  "Run advertising, affiliate, and hybrid campaigns",
  "Track performance, conversions, and payouts",
  "Manage briefs, approvals, and milestones in one place",
  "Hire influencers and scale with agencies",
];

const RegisterModal = ({ isOpen, onClose, onRegisterAs }) => {
  const modalRef = useRef(null);
  const scrollerRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const scrollY = window.scrollY;
    const html = document.documentElement;
    const { body } = document;
    const prev = {
      htmlOverflow: html.style.overflow,
      htmlOverscroll: html.style.overscrollBehavior,
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyLeft: body.style.left,
      bodyRight: body.style.right,
      bodyWidth: body.style.width,
    };

    html.style.overflow = "hidden";
    html.style.overscrollBehavior = "none";
    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.top = `-${scrollY}px`;
    body.style.left = "0";
    body.style.right = "0";
    body.style.width = "100%";

    const onTouchMove = (event) => {
      const scroller = scrollerRef.current;
      if (scroller && scroller.contains(event.target)) return;
      event.preventDefault();
    };
    document.addEventListener("touchmove", onTouchMove, { passive: false });

    return () => {
      document.removeEventListener("touchmove", onTouchMove);
      html.style.overflow = prev.htmlOverflow;
      html.style.overscrollBehavior = prev.htmlOverscroll;
      body.style.overflow = prev.bodyOverflow;
      body.style.position = prev.bodyPosition;
      body.style.top = prev.bodyTop;
      body.style.left = prev.bodyLeft;
      body.style.right = prev.bodyRight;
      body.style.width = prev.bodyWidth;
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") onClose();
    };
    if (isOpen) document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[80] flex h-dvh max-h-dvh flex-col overflow-hidden overscroll-none bg-[#0b0c10]">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.12]"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
        }}
      />
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-40 left-1/2 h-[28rem] w-[28rem] -translate-x-1/2 rounded-full bg-orange-500/15 blur-3xl" />
        <div className="absolute bottom-0 left-1/3 h-[20rem] w-[20rem] rounded-full bg-orange-500/10 blur-3xl" />
      </div>

      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="register-modal-title"
        className="relative mx-auto flex h-full w-full max-w-5xl min-h-0 flex-col px-4 py-4 sm:px-6 sm:py-5"
      >
        <div className="flex shrink-0 items-center justify-between pb-3 sm:pb-4">
          <button
            type="button"
            onClick={onClose}
            className="text-sm text-gray-400 transition hover:text-white"
          >
            ← Back to website
          </button>
          {/* <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-gray-400 transition hover:bg-white/10 hover:text-white"
            aria-label="Close"
          >
            <FaClose className="h-4 w-4" />
          </button> */}
        </div>

        <div
          ref={scrollerRef}
          className="flex min-h-0 flex-1 flex-col justify-start overflow-y-auto overscroll-contain md:justify-center md:overflow-hidden"
        >
          <div className="w-full">
            <div className="mb-5 text-center sm:mb-6">
              <h2
                id="register-modal-title"
                className="text-2xl font-bold text-white sm:text-3xl md:text-4xl"
              >
                Welcome To <span className="text-orange-500">Infuber</span>
              </h2>
              <p className="mt-1.5 text-sm text-gray-400 sm:text-base">
                Choose your role to access the appropriate Features
              </p>
            </div>

            <div className="mx-auto grid w-full max-w-4xl grid-cols-1 items-start gap-4 pb-10 md:grid-cols-2 md:gap-6 md:pb-0">
              <RoleCard
                icon={FaUser}
                prefix="I'm a"
                title="Creator"
                description="Create content, join brand campaigns, and earn through DMs, affiliates, and collaborations."
                features={CREATOR_FEATURES}
                cta="Login as Creator"
                onSelect={() => onRegisterAs("influencer")}
              />
              <RoleCard
                icon={FaBuilding}
                prefix="I'm a"
                title="Brand / Agencies"
                description="Discover creators, run campaigns, and grow your business with verified influencer marketing."
                features={BRAND_FEATURES}
                cta="Login as Brand"
                onSelect={() => onRegisterAs("brand")}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const RoleCard = ({ icon: Icon, prefix, title, description, features, cta, onSelect }) => (
  <div className="group flex h-auto flex-col rounded-3xl border border-transparent bg-[#16181f] p-5 text-center transition-all duration-200 hover:border-orange-500/80 hover:shadow-[0_0_0_1px_rgba(249,115,22,0.35)] sm:p-6">
    <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full border border-white/15 bg-white/5 text-white sm:h-14 sm:w-14">
      <Icon className="h-6 w-6 sm:h-7 sm:w-7" />
    </div>

    <h3 className="text-lg font-semibold text-white sm:text-xl">
      <span className="italic font-normal text-gray-300">{prefix} </span>
      {title}
    </h3>
    <p className="mt-2 text-xs leading-relaxed text-gray-400 sm:text-sm">
      {description}
    </p>

    <ul className="mt-4 space-y-2 text-left text-xs text-gray-300 sm:space-y-2.5 sm:text-sm">
      {features.map((item) => (
        <li key={item} className="flex items-start gap-2.5">
          <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-white" />
          <span>{item}</span>
        </li>
      ))}
    </ul>

    <button
      type="button"
      onClick={onSelect}
      className="mt-4 w-full rounded-xl bg-orange-500 py-2.5 text-sm font-bold text-black transition hover:bg-orange-400 sm:mt-5 sm:py-3"
    >
      {cta}
    </button>
  </div>
);

export default RegisterModal;
